import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";
import { royalMomentumTasksAndFollowupsApi } from "./royalMomentumTasksAndFollowupsApi";
import { royalMomentumGrowthPrioritiesApi } from "./royalMomentumGrowthPrioritiesApi";

// =============================================================================
// ROYAL MOMENTUM - Meetings API
// =============================================================================

export const royalMomentumMeetingsApi = createApi({
    reducerPath: "royalMomentumMeetingsApi",
    baseQuery: apiRequestBaseQuery,
    tagTypes: ["Meetings"],
    endpoints: (builder) => ({
        // Get Meetings (upcoming or recent) — V2.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        getProMeetings: builder.query({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getMeetingsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || null,
                        show: params.show || "upcoming",
                    },
                };
            },
            providesTags: ["Meetings"],
            // Meetings are user-created records, so a stale list is always wrong. Dropping the
            // cache entry as soon as the last subscriber unmounts makes every remount refetch,
            // which covers the create-meeting → back-to-dashboard path even when the write
            // happened in another tab or outside this slice.
            keepUnusedDataFor: 0,
        }),

        // Add Meeting — V2. Lives here rather than in the Meeting Hub step-4 hook so that
        // creating a meeting invalidates the "Meetings" tag; a raw apiRequest cannot.
        // `agency_id` replaces `travel_agency_uid`; value is params.travelAgencyUid
        // (= selectedBrand.uid = agency_id_no_r).
        addMeeting: builder.mutation({
            query: (params) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.addMeetingV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: params.travelAgencyUid || "",
                        meeting_title: params.meetingTitle || "",
                        meeting_type: params.meetingType || "VIRTUAL",
                        meeting_date: params.meetingDate || "",
                        meeting_start_time: params.meetingStartTime || "",
                        meeting_end_time: params.meetingEndTime || "",
                        meeting_link: params.meetingLink ?? null,
                        address: params.address ?? null,
                        agenda: params.agenda || "",
                        agenda_point: params.agendaPoint || [],
                        is_link_generated: params.isLinkGenerated ?? 1,
                        internal_attendee_uids: params.internalAttendeeUids || "",
                        external_attendee_uids: params.externalAttendeeUids || "",
                    },
                };
            },
            invalidatesTags: ["Meetings"],
            // Tags never cross a `createApi` boundary, so the meeting-derived reads that live in
            // other slices have to be invalidated by hand once the write lands:
            //   - tasks/follow-ups → its own meeting dropdown (getListMeetingsByAgency)
            //   - growth priorities → getProMeetingsLoggedIn60Days
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    return;
                }
                dispatch(royalMomentumTasksAndFollowupsApi.util.invalidateTags(["Meetings"]));
                dispatch(
                    royalMomentumGrowthPrioritiesApi.util.invalidateTags(["GrowthPriorities"])
                );
            },
        }),
    }),
});

// Export hooks for usage in components
export const { useGetProMeetingsQuery, useAddMeetingMutation } = royalMomentumMeetingsApi;
